import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../common/sidebar";
import Footer from "./footer";

const ICONS = {
  subscription: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  training: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  'mobile-app': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  ),
  'desktop-app': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
  blog: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
  ),
  jobs: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  ),
  help: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  import: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  achievements: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
    </svg>
  ),
};

const FEATURES = {
  '/subscription': {
    name: 'Subscription & Billing',
    get description() { return tr("Manage your plan, view billing history, and update payment methods — all in one place."); },
    cta: 'View Plans',
    ctaHref: '#',
  },
  '/training': {
    name: 'Training & Onboarding',
    get description() { return tr("Step-by-step guides, video tutorials, and onboarding flows to get your team up to speed fast."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
  '/mobile-app': {
    name: 'Mobile App',
    get description() { return tr("Track time, manage projects, and check reports on the go from iOS and Android."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
  '/desktop-app': {
    name: 'Desktop App',
    get description() { return tr("A native desktop experience for macOS and Windows with idle detection and offline support."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
  '/blog': {
    name: 'Blog & Resources',
    get description() { return tr("Productivity tips, product updates, and time management guides from the Minutezilla team."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
  '/jobs': {
    name: 'Careers',
    get description() { return tr("We're building something great and always looking for talented people to join the team."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
  '/help': {
    name: 'Help Center',
    get description() { return tr("Searchable documentation, FAQs, and live chat support to answer your questions instantly."); },
    cta: 'Contact Support',
    ctaHref: 'mailto:support@minutezilla.com',
  },
  '/import': {
    name: 'Import & Export',
    get description() { return tr("Bring in data from Toggl, Harvest, or CSV files, and export your time logs in any format."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
  '/achievements': {
    name: 'Achievements',
    get description() { return tr("Earn badges, hit milestones, and celebrate your team's productivity wins."); },
    cta: 'Notify Me',
    ctaHref: '#',
  },
};

export default function ComingSoonPage() {
  useTranslation();
  const location = useLocation();
  const history = useNavigate();
  const key = location.pathname.replace('/', '');
  const feature = FEATURES[location.pathname] || {
    name: 'Coming Soon',
    get description() { return tr("This feature is under active development and will be available soon."); },
    cta: 'Go Back',
    ctaHref: null,
  };
  const icon = ICONS[key] || ICONS['achievements'];

  const handleCta = () => {
    if (!feature.ctaHref || feature.cta === 'Go Back') {
      history(-1);
    }
  };

  return (
    <>
      <Sidebar />
      <div className="content-wrap" id="wrapper">
        <div className="main cs-page-main">
          <div className="cs-container">
            {/* Back link */}
            <button className="cs-back-btn" onClick={() => history(-1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>{tr("Back")} </button>

            <div className="cs-card">
              {/* Decorative top bar */}
              <div className="cs-card-accent" />

              <div className="cs-card-body">
                {/* Icon */}
                <div className="cs-icon-wrap">
                  {icon}
                </div>

                {/* Badge */}
                <span className="cs-badge">{tr("Coming Soon")}</span>

                {/* Heading */}
                <h1 className="cs-heading">{tr(feature.name)}</h1>

                {/* Description */}
                <p className="cs-description">{feature.description}</p>

                {/* Divider */}
                <div className="cs-divider" />

                {/* Info row */}
                <div className="cs-info-row">
                  <div className="cs-info-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span>{tr("In development")}</span>
                  </div>
                  <div className="cs-info-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <span>{tr("Launching soon")}</span>
                  </div>
                  <div className="cs-info-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <span>{tr("All plans")}</span>
                  </div>
                </div>

                {/* CTA */}
                <div className="cs-cta-row">
                  {feature.ctaHref && feature.ctaHref !== '#' ? (
                    <a href={feature.ctaHref} className="cs-btn-primary">{tr(feature.cta)}</a>
                  ) : (
                    <button className="cs-btn-primary" onClick={handleCta}>{tr(feature.cta)}</button>
                  )}
                  <button className="cs-btn-secondary" onClick={() => history('/dashboard')}>{tr("Back to Dashboard")} </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
